import { check_audio, check_audio_telugu } from "./_submissionCheck.js";
import { syncStudentAttemptToCloudSql } from "./cloudSqlSync";
import type { StudentRecord, Submission, Word } from "./backendTypes.js";
import { classifySketch } from "./inference_img.js";
import { transcribe } from "./transcriberEn.js";
const DB_NAME: string = "vocabDB";
export const DB_VERSION: number = 12;

let dbInstance: IDBDatabase | null = null;

export const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }
    const request: IDBOpenDBRequest = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      const target = event.target as IDBOpenDBRequest;
      console.error(`Database error: ${target.error}`);
      reject(`Database error: ${target.error}`);
    };

    request.onsuccess = (event) => {
      const target = event.target as IDBOpenDBRequest;
      dbInstance = target.result;
      console.log("Database opened successfully.");
      localStorage.setItem("dbVersion", DB_VERSION.toString());
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const target = event.target as IDBOpenDBRequest;
      const db = target.result as IDBDatabase;
      console.log("Upgrading database...");

      // Student profiles are embedded under school.class[*].sections[*].
      if (db.objectStoreNames.contains("student")) {
        db.deleteObjectStore("student");
        console.log("deleted deprecated student object store");
      }
      if (!db.objectStoreNames.contains("word")) {
        db.createObjectStore("word", {
          keyPath: "english",
        });
        console.log("created word object store");
      }
      if (db.objectStoreNames.contains("student_learning")) {
        db.deleteObjectStore("student_learning");
        console.log("recreated student learning object store for new schema");
      }
      db.createObjectStore("student_learning", {
        keyPath: "studentId",
      });
      console.log("created student learning object store");

      if (!db.objectStoreNames.contains("class")) {
        db.createObjectStore("class", {
          keyPath: "classId",
          autoIncrement: true,
        });
        console.log("created class object store");
      }
      if (!db.objectStoreNames.contains("model")) {
        db.createObjectStore("model", {
          keyPath: "modelUse",
        });
        console.log("created model object store");
      }

      if (!db.objectStoreNames.contains("school")) {
        db.createObjectStore("school", {
          keyPath: "schoolId",
          autoIncrement: true,
        });
        console.log("created school object store");
      }
    };
  });
};

export const saveData = async <T>(
  data: T,
  storeName: string
): Promise<string> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readwrite"
    );
    const store: IDBObjectStore = transaction.objectStore(storeName);
    const request: IDBRequest = store.put(data);

    request.onsuccess = () => {
      console.log("Data saved successfully");
      resolve("Data saved successfully");
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.error(`Error saving data: ${target.error}`);
      reject(`Error saving data: ${target.error?.message}`);
    };
  });
};

export const getData = async <T>(storeName: string): Promise<T | undefined> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readonly"
    );

    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Data retrieved: ${target.result}`);
      resolve(target.result as T | undefined);
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Error retrieving data: ${target.error}`);
      reject(`Error retrieving data: ${target.error?.message}`);
    };
  });
};
export const getBlobEntry = async (id: string): Promise<Blob | null> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["model"],
      "readonly"
    );
    const store = transaction.objectStore("model");

    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result;
      resolve(result?.modelFile);
    };
    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      reject(
        new Error(
          `Error retrieving blob with ID ${id}: ${target.error?.message}`
        )
      );
    };
  });
};

type ModelEntry = {
  modelUse: string;
  modelFile?: Blob;
  dataFile?: Blob;
};

export const getModelEntry = async (id: string): Promise<ModelEntry | null> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["model"],
      "readonly"
    );
    const store = transaction.objectStore("model");

    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as ModelEntry | undefined;
      resolve(result ?? null);
    };
    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      reject(
        new Error(
          `Error retrieving model entry with ID ${id}: ${target.error?.message}`
        )
      );
    };
  });
};
export const clearStore = async (storeName: string): Promise<string> => {
  if (!dbInstance) await openDB();

  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      [storeName],
      "readwrite"
    );

    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => {
      console.log("Store cleared successfully");
      resolve("Store cleared successfully");
    };

    request.onerror = (event) => {
      const target = event.target as IDBRequest;
      console.log(`Error clearing store: ${target.error}`);
      reject(`Error clearing store: ${target.error?.message}`);
    };
  });
};

export const deleteStudentLearningByStudentId = async (
  studentId: number
): Promise<void> => {
  if (!dbInstance) await openDB();

  await new Promise<void>((resolve) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["student_learning"],
      "readwrite"
    );
    const store = transaction.objectStore("student_learning");
    const request = store.delete(studentId);

    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
  });
};

export const storeWords = async (words: Array<Word>): Promise<string> => {
  if (!dbInstance) await openDB();

  // Fetch all blobs first
  const preparedWords = await Promise.all(
    words.map(async (word) => {
      try {
        const imageBlob = await fetchAsBlob(word.image_url);
        console.log("downloaded image, starting audio");
        const audio_telugu = await fetchAsBlob(word.audio_telugu_url);
        console.log("downloaded audio, starting audio");
        const audio_english = await fetchAsBlob(word.audio_english_url);
        console.log("downloaded all");

        console.log("Retrieved image and audio files for word.");

        return {
          ...word,
          image: imageBlob,
          audio_telugu: audio_telugu,
          audio_english: audio_english,
        };
      } catch (error) {
        console.log(`Failed to fetch resources for ${word.english}:`, error);
        throw new Error(
          `Error fetching resources for ${word.english}: ${error}`
        );
      }
    })
  );

  console.log(preparedWords);
  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["word"],
      "readwrite"
    );

    for (const word of preparedWords) {
      console.log(`trying to store ${word.english} in database`);
      const store = transaction.objectStore("word");

      transaction.oncomplete = () => {
        console.log("Transaction to store word completed successfully.");
        resolve("Word stored successfully");
      };

      transaction.onerror = (error) => {
        const target = error.target as IDBTransaction;
        console.log(`Error on transaction to store words: ${target.error}`);
        reject(`Transaction error: ${target.error?.message}`);
      };
      const request = store.put(word);
      request.onsuccess = () => {
        console.log("put word in indexeddb");
      };
      request.onerror = (error) => {
        const target = error.target as IDBRequest;
        console.log(`Skipped word ${word.english}: ${target.error?.message}`);
      };
    }
  });
};

// Fetch image from GCS url
const fetchAsBlob = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
  return await response.blob();
};

type Class = {
  grade: "I" | "II" | "III";
  className: "A" | "B" | "C" | "D" | "E" | "F";
};

type AttemptStatus = "not attempted" | "correct" | "wrong" | "skipped";
type AttemptStatusLegacy = AttemptStatus | 0 | 1 | 2 | 3;

type StudentWordAttempt = {
  spoken_en: AttemptStatusLegacy;
  drawn: AttemptStatusLegacy;
  spoken_te: AttemptStatusLegacy;
  date: string;
  count: number;
};

type StudentWordLearning = {
  wordEnglish: string;
  attempts: StudentWordAttempt[];
};

type ToyShelfProgress = {
  slots: number[];
  lastCollectedSlot: number | null;
};

type Collectible = {
  [key: string]: number;
};

export type CashierGameSession = {
  date: string;
  score: number;
  questionsCorrect: number;
  questionsTotal: number;
  hintsUsed: number;
  bestStreak: number;
};

export type CashierGameProgress = {
  totalScore: number;
  highScore: number;
  bestStreak: number;
  roundsPlayed: number;
  sessions: CashierGameSession[];
};

type StudentLearningRecord = {
  studentId: number;
  words: StudentWordLearning[];
  toyProgress?: ToyShelfProgress;
  collectible?: Collectible;
  cashierGame?: CashierGameProgress;
};

const TOY_SLOT_COUNT = 10;
const TOY_TARGET_PER_SLOT = 10;

const createDefaultToyProgress = (): ToyShelfProgress => ({
  slots: Array.from({ length: TOY_SLOT_COUNT }, () => 0),
  lastCollectedSlot: null,
});

const normalizeToyProgress = (value: unknown): ToyShelfProgress => {
  const fallback = createDefaultToyProgress();
  if (!value || typeof value !== "object") return fallback;

  const maybeProgress = value as Partial<ToyShelfProgress>;
  const slots = Array.isArray(maybeProgress.slots)
    ? maybeProgress.slots
        .slice(0, TOY_SLOT_COUNT)
        .map((count) =>
          Number.isFinite(count)
            ? Math.max(0, Math.min(TOY_TARGET_PER_SLOT, Number(count)))
            : 0
        )
    : [];

  while (slots.length < TOY_SLOT_COUNT) {
    slots.push(0);
  }

  const lastCollectedSlot =
    typeof maybeProgress.lastCollectedSlot === "number" &&
    maybeProgress.lastCollectedSlot >= 0 &&
    maybeProgress.lastCollectedSlot < TOY_SLOT_COUNT
      ? maybeProgress.lastCollectedSlot
      : null;

  return {
    slots,
    lastCollectedSlot,
  };
};

const createDefaultCollectible = (): Collectible => ({});

const normalizeCollectible = (value: unknown): Collectible => {
  if (!value || typeof value !== "object") return createDefaultCollectible();

  const output: Collectible = {};
  for (const [key, rawCount] of Object.entries(value as Record<string, unknown>)) {
    const count = Number(rawCount);
    if (!Number.isFinite(count) || count < 0) continue;
    output[key] = Math.floor(count);
  }

  return output;
};

const normalizeAttemptStatus = (status: AttemptStatusLegacy): AttemptStatus => {
  if (typeof status === "string") return status;
  if (status === 1) return "correct";
  if (status === 2) return "wrong";
  if (status === 3) return "skipped";
  return "not attempted";
};

const getStudentsFromSchoolStore = async (): Promise<StudentRecord[]> => {
  const schools =
    (await getData<
      Array<{
        class?: Record<
          number,
          { classId: number; sections: Record<string, StudentRecord[]> }
        >;
      }>
    >("school")) ?? [];

  return schools.flatMap((school) =>
    Object.values(school.class ?? {}).flatMap((classEntry) =>
      Object.values(classEntry.sections ?? {}).flatMap(
        (sectionStudents) => sectionStudents ?? []
      )
    )
  );
};

// Return list of all classes
export const getAllClasses = async (): Promise<Class> => {
  if (!dbInstance) await openDB();
  return new Promise((resolve, reject) => {
    const transaction = (dbInstance as IDBDatabase).transaction(
      ["class"],
      "readonly"
    );
    const store = transaction.objectStore("class");
    const request = store.getAll();

    request.onerror = (e) => {
      const target = e.target as IDBRequest;
      console.log(`Error while getting all classes: ${target.error?.message}`);
      reject(`Error while getting all classes: ${target.error?.message}`);
    };

    // TODO: fix type in resolve
    request.onsuccess = (e) => {
      const target = e.target as IDBRequest;
      console.log("Got all existing classes from database.");
      resolve(target.result);
    };
  });
};
// Get all students for a class
export const getStudentProfiles = async (class_id: number) => {
  if (!dbInstance) await openDB();

  const students = await getStudentsFromSchoolStore();
  console.log(`Got all student profiles for class ${class_id}`);
  return students.filter((student) => student.classId === class_id);
};

// Return list of next words for a student to practice
// TODO: return a mix of old and new words
export const getNextWords = async (studentId: number, count = 10) => {
  if (!dbInstance) await openDB();

  const transaction = (dbInstance as IDBDatabase).transaction(
    ["word", "student_learning"],
    "readonly"
  );

  const learningStore = transaction.objectStore("student_learning");
  const wordStore = transaction.objectStore("word");

  // 1. Get all words the student has already seen
  const learningData =
    studentId > 0
      ? await new Promise<StudentLearningRecord | undefined>((res, rej) => {
          const request = learningStore.get(studentId);
          request.onsuccess = () => res(request.result as StudentLearningRecord);
          request.onerror = () => {
            console.log(
              `Error while finding all words learned by a student: ${request.error}`
            );
            rej(request.error);
          };
        })
      : undefined;
  const seenWords = new Set(
    (learningData?.words ?? [])
      .filter((word) => word.attempts.some((attempt) => attempt.count > 0))
      .map((word) => word.wordEnglish)
  );

  // 2. Get all words from the word store
  const allWords = await new Promise<Word[]>((res, rej) => {
    const request = wordStore.getAll();
    request.onsuccess = () => res(request.result);
    request.onerror = () => {
      console.log(`Error while getting all words: ${request.error}`);
      rej(request.error);
    };
  });

  // 3. Filter out seen words
  const unseenWords = allWords.filter((word: Word) => !seenWords.has(word.english));

  // 4. Shuffle and pick `count` words.
  // If the student has seen all words, fall back to all words so practice can continue.
  // TODO: change order the words get picked in
  const sourceWords = unseenWords.length > 0 ? unseenWords : allWords;
  const shuffled = [...sourceWords].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

/**
 * Evaluates a student's submission using the appropriate on-device AI model,
 * then updates `student_learning` using one per-student record with per-word
 * daily attempt summaries.
 *
 * Returns `true` if the AI judged the answer correct, `false` otherwise.
 */
export const submit = async (submission: Submission): Promise<boolean> => {
  if (!dbInstance) await openDB();

  const isSkip = submission.skip;
  let correct = false;
  const submissionFile = submission.file;

  if (!isSkip) {
    if (!submissionFile) {
      console.warn("Submission file is missing for non-skip attempt.");
    } else {
    switch (submission.type) {
      case "audio_english": {
        const resultEng = await transcribe(
          submissionFile,
          submission.wordEnglish
        );
        console.log(resultEng);
        correct = resultEng.isCorrect;
        break;
      }
      case "audio_telugu": {
        const wordData = await new Promise<Word | undefined>((res) => {
          const req = (dbInstance as IDBDatabase)
            .transaction(["word"], "readonly")
            .objectStore("word")
            .get(submission.wordEnglish);
          req.onsuccess = () => res(req.result as Word);
          req.onerror = () => res(undefined);
        });
        correct = await check_audio_telugu(
          submissionFile,
          wordData?.telugu ?? submission.wordEnglish
        );
        break;
      }
      case "image": {
        const imageModelEntry = await getModelEntry("image");
        if (!imageModelEntry?.modelFile) {
          console.warn("image model blob not found in IndexedDB.");
          correct = false;
          break;
        }
        if (!imageModelEntry.dataFile) {
          console.warn("image model external data blob not found in IndexedDB.");
          correct = false;
          break;
        }
        const result = await classifySketch(
          imageModelEntry.modelFile,
          imageModelEntry.dataFile,
          submissionFile,
          submission.wordEnglish
        );
        console.log(result);
        correct = "isMatch" in result ? result.isMatch : false;
        break;
      }
    }
    }
  }

  const status: AttemptStatus = isSkip ? "skipped" : correct ? "correct" : "wrong";
  const today = new Date().toISOString().slice(0, 10);
  const modeKey: keyof Pick<StudentWordAttempt, "spoken_en" | "spoken_te" | "drawn"> =
    submission.type === "audio_english"
      ? "spoken_en"
      : submission.type === "audio_telugu"
      ? "spoken_te"
      : "drawn";

  if (submission.studentId <= 0) {
    console.log("Guest submission detected; skipping student_learning persistence.");
    return correct;
  }

  await new Promise<void>((resolve) => {
    const tx = (dbInstance as IDBDatabase).transaction(
      ["student_learning"],
      "readwrite"
    );
    const store = tx.objectStore("student_learning");
    const getRequest = store.get(submission.studentId);

    getRequest.onerror = (e) => {
      const t = e.target as IDBRequest;
      console.log(`Error reading student_learning: ${t.error?.message}`);
      resolve();
    };

    getRequest.onsuccess = () => {
      const existing =
        (getRequest.result as StudentLearningRecord | undefined) ?? {
          studentId: submission.studentId,
          words: [],
          toyProgress: createDefaultToyProgress(),
          collectible: createDefaultCollectible(),
        };

      existing.words = Array.isArray(existing.words) ? existing.words : [];
      existing.toyProgress = normalizeToyProgress(existing.toyProgress);
      existing.collectible = normalizeCollectible(existing.collectible);

      let wordRecord = existing.words.find(
        (word) => word.wordEnglish === submission.wordEnglish
      );
      if (!wordRecord) {
        wordRecord = { wordEnglish: submission.wordEnglish, attempts: [] };
        existing.words.push(wordRecord);
      }

      let dayRecord = wordRecord.attempts.find((attempt) => attempt.date === today);
      if (!dayRecord) {
        dayRecord = {
          spoken_en: "not attempted",
          spoken_te: "not attempted",
          drawn: "not attempted",
          date: today,
          count: 0,
        };
        wordRecord.attempts.push(dayRecord);
      }

      const previousStatus = normalizeAttemptStatus(dayRecord[modeKey]);
      dayRecord[modeKey] =
        previousStatus === "correct" && status !== "correct"
          ? "correct"
          : status;
      dayRecord.count += 1;

      const putRequest = store.put(existing);
      putRequest.onerror = (e) => {
        const t = e.target as IDBRequest;
        console.log(`Error saving to student_learning: ${t.error?.message}`);
        resolve();
      };
      putRequest.onsuccess = () => {
        console.log("Saved to student_learning.");
        void syncStudentAttemptToCloudSql({
          studentId: submission.studentId,
          wordEnglish: submission.wordEnglish,
          type: submission.type,
          status,
          attemptDate: today,
          attemptCount: 1,
          toyProgress: existing.toyProgress,
          collectible: existing.collectible,
        });
        resolve();
      };
    };
  });

  return correct;
};

// Return student's learning progress using studentId
// TODO: types
export const studentLearningProgress = async (
  studentId: number
): Promise<LearningProgress> => {
  if (!dbInstance) await openDB();

  const students = await getStudentsFromSchoolStore();
  const studentName =
    students.find((student) => student.studentId === studentId)?.studentName ??
    "Unknown Student";

  const learningData = await new Promise<StudentLearningRecord | undefined>(
    (res, rej) => {
      const transaction = (dbInstance as IDBDatabase).transaction(
        ["student_learning"],
        "readonly"
      );
      const learningStore = transaction.objectStore("student_learning");
      const request = learningStore.get(studentId);

      request.onsuccess = () => {
        res(request.result as StudentLearningRecord);
      };
      request.onerror = () => rej(request.error);
    }
  );

  const progress: LearningProgress = {
    studentId: studentId,
    studentName: studentName,
    wordsSeen: 0,
    wordsAttempted: 0,
    wordsSkipped: 0,
    wordsCorrect: 0,
  };

  for (const word of learningData?.words ?? []) {
    let seenForWord = false;

    for (const attempt of word.attempts) {
      const modalityStatuses: AttemptStatus[] = [
        normalizeAttemptStatus(attempt.spoken_en),
        normalizeAttemptStatus(attempt.spoken_te),
        normalizeAttemptStatus(attempt.drawn),
      ];

      for (const state of modalityStatuses) {
        if (state === "not attempted") continue;
        seenForWord = true;
        if (state === "skipped") progress.wordsSkipped++;
        if (state === "correct" || state === "wrong") progress.wordsAttempted++;
        if (state === "correct") progress.wordsCorrect++;
      }
    }

    if (seenForWord) {
      progress.wordsSeen++;
    }
  }

  return progress;
};

type LearningProgress = {
  studentId: number;
  studentName: string;
  wordsSeen: number;
  wordsSkipped: number;
  wordsAttempted: number;
  wordsCorrect: number;
};
